import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/auth';
import { getDefaultRoute } from '@/utils/permissions';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import type { Role } from '@/types';

export function useAuth() {
  const { user, accessToken, isAuthenticated, setAuth, logout: storeLogout } = useAuthStore();
  const navigate = useNavigate();

  const loginMutation = useMutation({
    mutationFn: ({ identifier, password }: { identifier: string; password: string }) =>
      authApi.login(identifier, password),
    onSuccess: (data) => {
      if (data.requiresOTP) {
        return; // OTP step handled by component
      }
      if (data.user && data.accessToken && data.refreshToken) {
        setAuth(data.user, data.accessToken, data.refreshToken);
        connectSocket();
        const route = getDefaultRoute(data.user.role as Role);
        void navigate({ to: route });
        toast.success(`Welcome, ${data.user.name}!`);
      }
    },
    onError: (error: unknown) => {
      const code = (error as { response?: { data?: { error?: { code: string } } } })?.response?.data?.error?.code;
      if (code === 'ACCOUNT_LOCKED') {
        toast.error('Account locked. Try again in 15 minutes.');
      } else if (code === 'ACCOUNT_INACTIVE') {
        toast.error('Account is inactive. Contact admin.');
      } else {
        toast.error('Invalid credentials. Please try again.');
      }
    },
  });

  const otpMutation = useMutation({
    mutationFn: ({ identifier, otp }: { identifier: string; otp: string }) =>
      authApi.verifyOTP(identifier, otp),
    onSuccess: (data) => {
      if (data.user && data.accessToken && data.refreshToken) {
        setAuth(data.user, data.accessToken, data.refreshToken);
        connectSocket();
        const route = getDefaultRoute(data.user.role as Role);
        void navigate({ to: route });
        toast.success('Verified successfully!');
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      if (accessToken) await authApi.logout(accessToken);
    },
    onSettled: () => {
      disconnectSocket();
      storeLogout();
      void navigate({ to: '/login' });
      toast.success('Logged out successfully');
    },
  });

  return {
    user,
    isAuthenticated,
    role: user?.role as Role | undefined,
    login: loginMutation.mutate,
    loginPending: loginMutation.isPending,
    verifyOTP: otpMutation.mutate,
    otpPending: otpMutation.isPending,
    logout: logoutMutation.mutate,
  };
}
